import { FC, useEffect, useMemo, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Box, Drawer, IconButton, Tooltip } from "@mui/material";
import { FilterBar, FilterSearch } from "./InvFilters";
import Filters from "../Filters/Filters";
import EditIcon from "@mui/icons-material/Edit";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import BarcodeLabelDrawer, { BarcodeLabelItem } from "./BarcodeLabelDrawer";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import CustomDataGrid from "../DataGrid/DataGrid";
import InventoryEmptyState from "./InventoryEmptyState";
import BatchForm from "./BatchForm";
import SkuSelect from "./pickers/SkuSelect";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchBatches } from "../../store/batchStore/batchThunk";
import { ApiStatus, defaultRowsPerPageOptions } from "../../utils/api.types";
import { Utils } from "../../utils/common-utils";

const BatchList: FC = () => {
  const dispatch = useAppDispatch();
  const { batches, rowCount, status } = useAppSelector((state) => state.batches);
  const { selectedAccount } = useAppSelector((state) => state.auth);

  // Filters
  const [skuId, setSkuId] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  // Mfg / Expiry ranges (from the shared Filters date-range dropdown).
  const [mfgFrom, setMfgFrom] = useState("");
  const [mfgTo, setMfgTo] = useState("");
  const [expiryFrom, setExpiryFrom] = useState("");
  const [expiryTo, setExpiryTo] = useState("");
  // Bumped on reset to remount each date-range dropdown back to "Select Date".
  const [mfgKey, setMfgKey] = useState(0);
  const [expiryKey, setExpiryKey] = useState(0);

  // Server-side pagination (0-based, mirrors Stock / Items lists).
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Right-side drawer form (edit only), same pattern as Location.
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editBatch, setEditBatch] = useState<any>(null);
  // View / print barcode label drawer (reuses the SKU label designer, seeded from this batch).
  const [labelItem, setLabelItem] = useState<BarcodeLabelItem | null>(null);

  const buildParams = () => {
    const p: string[] = [];
    if (skuId) p.push(`skuId=${skuId}`);
    if (batchNumber) p.push(`batchNumber=${encodeURIComponent(batchNumber)}`);
    if (mfgFrom) p.push(`mfgFrom=${mfgFrom}`);
    if (mfgTo) p.push(`mfgTo=${mfgTo}`);
    if (expiryFrom) p.push(`expiryFrom=${expiryFrom}`);
    if (expiryTo) p.push(`expiryTo=${expiryTo}`);
    p.push(`page=${page}`);
    p.push(`limit=${pageSize}`);
    return p.join("&");
  };

  const loadData = () => dispatch(fetchBatches(buildParams()));

  // Debounced load (covers the free-text batch-number filter without a request
  // per keystroke); selects/dates/pagination resolve on the same tick.
  useEffect(() => {
    const t = setTimeout(loadData, 350);
    return () => clearTimeout(t);
  }, [dispatch, skuId, batchNumber, mfgFrom, mfgTo, expiryFrom, expiryTo, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSkuChange = (id: string) => { setSkuId(id); setPage(0); };
  const handleBatchNumberChange = (v: string) => { setBatchNumber(v); setPage(0); };
  // Each range comes from a shared Filters dropdown emitting "start=..&end=..". Read
  // ONLY start/end; batch the updates (Filters calls this from a setTimeout, so
  // separate setStates would fire a partial first fetch).
  const handleMfgDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    const from = params.get("start") || "";
    const to = params.get("end") || "";
    unstable_batchedUpdates(() => { setMfgFrom(from); setMfgTo(to); setPage(0); });
  };
  const handleExpiryDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    const from = params.get("start") || "";
    const to = params.get("end") || "";
    unstable_batchedUpdates(() => { setExpiryFrom(from); setExpiryTo(to); setPage(0); });
  };
  const resetFilters = () => {
    setSkuId("");
    setBatchNumber("");
    setMfgFrom("");
    setMfgTo("");
    setExpiryFrom("");
    setExpiryTo("");
    // Remount both date-range dropdowns so they reinitialise to "Select Date".
    setMfgKey((k) => k + 1);
    setExpiryKey((k) => k + 1);
    setPage(0);
  };
  const handlePageSizeChange = (n: number) => { setPageSize(n); setPage(0); };

  const openEdit = (row: any) => {
    setEditBatch(row);
    setOpenDrawer(true);
  };

  // Seed the barcode-label designer from a batch row. Prices are the per-base-unit
  // rates (unitMrp / unitSalePrice) so the label matches the scanned base unit; the
  // barcode itself is the batch's own code for internal_batch SKUs, else the SKU code.
  const openLabel = (row: any) => {
    const sku = row.skuId || {};
    setLabelItem({
      _id: sku._id,
      batchId: sku.barcodeMode === "internal_batch" ? row._id : undefined,
      name: sku.name,
      itemCode: sku.itemCode,
      mrp: row.unitMrp ?? row.mrp,
      salePrice: row.unitSalePrice ?? row.salePrice,
      baseUnitName: sku.baseUnitName,
      unitConversions: sku.unitConversions,
      barcodeMode: sku.barcodeMode,
    });
  };

  const closeDrawer = (refresh: boolean) => {
    setOpenDrawer(false);
    setEditBatch(null);
    if (refresh) loadData();
  };

  // Price cell: the entered per-receiving-unit amount on top, and the derived
  // per-base-unit rate below (grey) so both are visible at a glance.
  const twoLinePrice = (amount: any, unitAmount: any, baseUnit?: string) => (
    <div style={{ lineHeight: 1.25 }}>
      <div>{Utils.formatNumber(amount ?? 0, false, true)}</div>
      {unitAmount > 0 ? (
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          ₹{Utils.formatNumber(unitAmount, false, true)} / {baseUnit || "unit"}
        </div>
      ) : null}
    </div>
  );

  // Expiry cell: red when on/after today (near/over-expiry batches stand out).
  const expiryCell = (d: any) => {
    if (!d) return <span>—</span>;
    const dt = new Date(d);
    const overdue = dt.getTime() <= Date.now();
    return <span style={{ color: overdue ? "#c33b3b" : "inherit", fontWeight: overdue ? 600 : 400 }}>{dt.toLocaleDateString()}</span>;
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "sku", headerName: "Item", flex: 1.4, minWidth: 150, valueGetter: (p: GridValueGetterParams) => p.row.skuId?.name || "" },
      { field: "batchNumber", headerName: "Batch #", flex: 1, minWidth: 120 },
      { field: "mfgDate", headerName: "Mfg", flex: 0.8, minWidth: 100, sortable: false, valueGetter: (p: GridValueGetterParams) => (p.row.mfgDate ? new Date(p.row.mfgDate).toLocaleDateString() : "—") },
      { field: "expiryDate", headerName: "Expiry", flex: 0.8, minWidth: 110, sortable: false, renderCell: ({ row }: GridValueGetterParams) => expiryCell(row.expiryDate) },
      { field: "mrp", headerName: "MRP (₹)", flex: 0.9, minWidth: 130, sortable: false, renderCell: ({ row }: GridValueGetterParams) => twoLinePrice(row.mrp, row.unitMrp, row.skuId?.baseUnitName) },
      { field: "salePrice", headerName: "Sale Price (₹)", flex: 0.9, minWidth: 130, sortable: false, renderCell: ({ row }: GridValueGetterParams) => twoLinePrice(row.salePrice, row.unitSalePrice, row.skuId?.baseUnitName) },
      { field: "purchasePrice", headerName: "Purchase Price (₹)", flex: 0.9, minWidth: 140, sortable: false, renderCell: ({ row }: GridValueGetterParams) => twoLinePrice(row.purchasePrice, row.unitPurchaseCost, row.skuId?.baseUnitName) },
      // { field: "supplier", headerName: "Supplier", flex: 1, minWidth: 120, valueGetter: (p: GridValueGetterParams) => p.row.supplierId?.name || "—" },
      { field: "grn", headerName: "GRN #", flex: 0.8, minWidth: 100, sortable: false, valueGetter: (p: GridValueGetterParams) => p.row.receivedVia?.grnNumber || "—" },
      {
        field: "actions",
        headerName: "Action",
        flex: 0.6,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) => (
          <>
            <Tooltip title="Edit batch">
              <IconButton
                style={{ color: "#49a563", backgroundColor: "#edf6ef", border: "0.1px solid #49a563", height: 35, width: 35, marginRight: 8 }}
                size="small"
                onClick={() => openEdit(row)}
              >
                <EditIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {row.skuId?.barcodeMode === "internal_batch" ? (
              <Tooltip title="View / print batch barcode">
                <IconButton
                  style={{ color: "#6b46c1", backgroundColor: "#f3effc", border: "0.1px solid #6b46c1", height: 35, width: 35 }}
                  size="small"
                  onClick={() => openLabel(row)}
                >
                  <QrCode2Icon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </>
        ),
      },
    ],
    []
  );

  const isEmpty = status !== ApiStatus.LOADING && (batches?.length || 0) === 0;

  return (
    <Box sx={{ width: "100%" }}>
      <FilterBar onReset={resetFilters}>
        <SkuSelect borderless width={260} value={skuId} onChange={(sku) => handleSkuChange(sku?._id || "")} label="Filter by item" />
        <FilterSearch value={batchNumber} onChange={handleBatchNumberChange} placeholder="Search batch no." width={200} />
        {/* Each Filters dropdown already has its own rounded border; just notch the
            label onto that existing border (no extra box). */}
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Mfg date</span>
          <Filters key={mfgKey} type="inventory-batch-mfg" dateOnly hidePartySearch hideFilter hideReset filterHandler={handleMfgDateFilter} />
        </div>
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Expiry date</span>
          <Filters key={expiryKey} type="inventory-batch-expiry" dateOnly hidePartySearch hideFilter hideReset filterHandler={handleExpiryDateFilter} />
        </div>
      </FilterBar>

      {isEmpty ? (
        <InventoryEmptyState
          height="75vh"
          icon={<Inventory2OutlinedIcon />}
          title="No Batches Found"
          description="No batches match the current filters. Batches are created when you post a Goods Receipt for batch-tracked items."
          caption="Need help? Check the Guide tab on inventory management"
        />
      ) : (
        <div style={{ height: "75vh", width: "100%" }}>
          <CustomDataGrid
            columns={columns}
            rows={batches}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(p: number) => setPage(p)}
            onPageSizeChange={handlePageSizeChange}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            getRowId={(row: any) => row._id || Math.random().toString()}
            loading={status === ApiStatus.LOADING}
          />
        </div>
      )}

      {/* Batch Form Drawer (edit) */}
      <Drawer disableEscapeKeyDown={false} anchor="right" open={openDrawer} onClose={() => closeDrawer(false)}>
        <BatchForm open={openDrawer} onClose={closeDrawer} batch={editBatch} />
      </Drawer>

      <BarcodeLabelDrawer
        open={!!labelItem}
        onClose={() => setLabelItem(null)}
        accountId={selectedAccount?._id}
        item={labelItem}
        companyInfo={{
          name: selectedAccount?.displayName || selectedAccount?.name,
          phone: selectedAccount?.whatsAppNumber,
          customerCare: selectedAccount?.whatsAppNumber || selectedAccount?.email,
        }}
      />
    </Box>
  );
};

export default BatchList;
